import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination, Popconfirm, Spin, message } from 'antd';
import { BellOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import Boton from '../Components/Atoms/Boton';
import ItemNotificacion from '../Components/Organisms/ItemNotificacion';
import {
  EVENTO_NOTIFICACIONES,
  agruparNotificaciones,
  avisarCambioNotificaciones,
  limpiarNotificaciones,
  marcarLeida,
  marcarTodasLeidas,
  obtenerLista
} from '../Utils/notificaciones';

const POR_PAGINA = 15;

const Notifications = () => {
  const navigate = useNavigate();
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState({ items: [], unread: 0, total: 0 });
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(() => {
    obtenerLista(pagina, POR_PAGINA)
      .then((lista) => {
        // Si se limpió o se borró la última de una página, se regresa a la anterior.
        if (lista.items.length === 0 && pagina > 1) setPagina(pagina - 1);
        else setDatos(lista);
      })
      .catch(() => message.error('No fue posible cargar las notificaciones.'))
      .finally(() => setCargando(false));
  }, [pagina]);

  useEffect(() => {
    setCargando(true);
    cargar();
  }, [cargar]);

  useEffect(() => {
    window.addEventListener(EVENTO_NOTIFICACIONES, cargar);
    return () => window.removeEventListener(EVENTO_NOTIFICACIONES, cargar);
  }, [cargar]);

  const abrir = (n) => {
    if (!n.read) {
      marcarLeida(n.id)
        .then(avisarCambioNotificaciones)
        .catch(() => {});
    }
    if (n.path) navigate(`/${n.path}`);
  };

  const todasLeidas = () =>
    marcarTodasLeidas()
      .then(avisarCambioNotificaciones)
      .catch(() => message.error('No fue posible marcarlas como leídas. Intenta de nuevo.'));

  const limpiar = () =>
    limpiarNotificaciones()
      .then(() => {
        setPagina(1);
        avisarCambioNotificaciones();
      })
      .catch(() => message.error('No fue posible limpiar las notificaciones. Intenta de nuevo.'));

  const grupos = agruparNotificaciones(datos.items).filter((g) => g.items.length > 0);

  return (
    <div className="notificaciones-pagina">
      <div className="notificaciones-pagina-cabecera">
        <h1>Notificaciones</h1>
        <div className="notificaciones-pagina-acciones">
          <Boton type="secondary" onClick={todasLeidas} disabled={datos.unread === 0}>
            <CheckOutlined /> Marcar todas como leídas
          </Boton>
          <Popconfirm
            title="¿Limpiar todas las notificaciones?"
            description="Se eliminarán de tu lista."
            okText="Limpiar"
            cancelText="Cancelar"
            onConfirm={limpiar}
            disabled={datos.total === 0}>
            <span>
              <Boton type="secondary" disabled={datos.total === 0}>
                <DeleteOutlined /> Limpiar
              </Boton>
            </span>
          </Popconfirm>
        </div>
      </div>

      {cargando ? (
        <div className="notificaciones-vacio">
          <Spin />
        </div>
      ) : grupos.length === 0 ? (
        <div className="notificaciones-vacio">
          <BellOutlined aria-hidden="true" />
          <p>No tienes notificaciones.</p>
        </div>
      ) : (
        <div className="notificaciones-pagina-lista">
          {grupos.map((grupo) => (
            <section key={grupo.clave} aria-label={grupo.titulo}>
              <h2 className="notificaciones-grupo">{grupo.titulo}</h2>
              {grupo.items.map((n) => (
                <ItemNotificacion key={n.id} notificacion={n} onAbrir={abrir} />
              ))}
            </section>
          ))}
        </div>
      )}

      {datos.total > POR_PAGINA && (
        <Pagination
          className="notificaciones-paginacion"
          current={pagina}
          pageSize={POR_PAGINA}
          total={datos.total}
          showSizeChanger={false}
          onChange={setPagina}
        />
      )}
    </div>
  );
};

export default Notifications;
